import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Typography,
} from '@material-ui/core';
import { Send, CloudDownload, Cancel as CancelIcon, FileCopy } from '@material-ui/icons';
import { Document } from '@roc/feature-types';
import { Button, ConfirmDialog, CopyText, PDFPreview, StandardDialog } from '@roc/ui';
import { CustomTaskDialog, TaskV2Type } from '@roc/feature-general-tasks';
import { ExtensionsInternalService, EXTENSION_STATUS, useExtensionsStore } from '@roc/feature-loan-details';
import { useStore } from '@roc/client-portal-shared/hooks';
import { LoanProcess } from '@roc/feature-utils';
import { makeStyles } from '@material-ui/core/styles';
import { useDocumentStore } from '../../../hooks/useDocumentStore';
import { ExtensionAgreementUpload } from './extensionAgreementUpload';

interface Props {
  open?: boolean;
  onClose?: () => void;
  document: Document;
}

const SIGNABLE_STATUSES = ['PENDING', 'SEND_FAILED'];

const useCopyTextStyles = makeStyles({
  copyText: {
    cursor: 'pointer',
  },
});

interface SignatureRequestRow {
  requestId: number;
  borrowerName: string;
  signingUrl?: string;
  status: string;
  createdDate: string;
}

export const ExtensionAgreementDialog = observer(({ open, onClose, document }: Props) => {
  const { globalStore } = useStore();
  const copyTextStyles = useCopyTextStyles();
  const { extensionsStore } = useExtensionsStore();
  const { documentStore } = useDocumentStore();
  const service = useRef(new ExtensionsInternalService()).current;
  const taskId = document?.taskId;
  const loanExtensionId = document?.extensionId;

  const refreshParentExtension = async () => {
    if (document?.loanId != null) {
      documentStore?.loanTodosStore?.fetchTodos(
        document.loanId,
        LoanProcess.EXTENSIONS,
        TaskV2Type.EXTENSION_ID,
        document.extensionId
      );
    }
    if (extensionsStore?.refreshSelectedExtension && document?.loanId != null) {
      await extensionsStore.refreshSelectedExtension(String(document.loanId));
    }
  };

  const handleDialogClose = () => {
    refreshParentExtension();
    onClose?.();
  };

  const [rows, setRows] = useState<SignatureRequestRow[]>([]);
  const [extension, setExtension] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (open && loanExtensionId) {
      loadStatus();
    }
  }, [open, loanExtensionId]);

  const parsePayload = (raw: unknown) =>
    typeof raw === 'string' ? JSON.parse(raw) : raw;

  const loadStatus = async () => {
    setLoading(true);
    try {
      const extensionResponse = await service.getLoanExtensionById(loanExtensionId);
      setExtension(parsePayload(extensionResponse?.data?.data) || null);

      const response = await service.getSignatureRequests(loanExtensionId);
      const parsed = parsePayload(response?.data?.data);
      setRows(parsed?.rows || []);
    } catch (error) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load extension agreement signing status',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!extension) {
      return;
    }
    setLoading(true);
    try {
      const response = await service.previewExtensionAgreement(extension);
      setPreviewData(response.data);
      setPreviewOpen(true);
    } catch (error) {
      globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while trying to open the document',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await service.resendPendingSignatureRequests(loanExtensionId);
      globalStore.notificationStore.showSuccessNotification({
        message: 'Signing links resent to the borrower(s).',
      });
      await loadStatus();
    } catch (error) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Failed to resend the signing links',
      });
      setLoading(false);
    }
  };

  const handleVoid = async () => {
    setShowVoidConfirm(false);
    setLoading(true);
    try {
      await service.markExtensionAgreementAsVoid(loanExtensionId);
      globalStore.notificationStore.showSuccessNotification({
        message: 'Extension agreement request voided.',
      });
      await loadStatus();
      await refreshParentExtension();
    } catch (error) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Failed to void the extension agreement request',
      });
      setLoading(false);
    }
  };

  const isSignable = (request: SignatureRequestRow) =>
    !!request.signingUrl && SIGNABLE_STATUSES.includes(String(request.status).toUpperCase());

  const isExtensionClosed =
    extension?.status === EXTENSION_STATUS.COMPLETED ||
    extension?.status === EXTENSION_STATUS.RETRACTED;

  const dialogContent = loading ? (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
      <CircularProgress />
    </Box>
  ) : (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table style={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '17%' }}>Borrower</TableCell>
                <TableCell style={{ width: '13%' }}>Status</TableCell>
                <TableCell>Signing Link</TableCell>
                <TableCell style={{ width: 150 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No signature requests found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(request => (
                  <TableRow key={request.requestId}>
                    <TableCell>{request.borrowerName}</TableCell>
                    <TableCell>{request.status}</TableCell>
                    <TableCell>
                      {isSignable(request) ? (
                        <Link
                          href={request.signingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={request.signingUrl}
                          noWrap
                          display="block"
                        >
                          {request.signingUrl}
                        </Link>
                      ) : ''}
                    </TableCell>
                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                      {isSignable(request) ? (
                        <CopyText
                          tooltipText="Copy signing link"
                          valueToCopy={request.signingUrl}
                          callBackFunction={() => undefined}
                          propStyles={copyTextStyles}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            testId={`copy-signing-link-${request.requestId}`}
                            startIcon={<FileCopy fontSize="small" />}
                          >
                            Copy Link
                          </Button>
                        </CopyText>
                      ) : ''}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
        <Button
          variant="contained"
          color="secondary"
          testId="preview-extension-agreement-button"
          onClick={handlePreview}
          startIcon={<CloudDownload />}
          disabled={loading || !extension}
        >
          Preview
        </Button>
        <Button
          variant="contained"
          color="secondary"
          testId="void-extension-agreement-button"
          onClick={() => setShowVoidConfirm(true)}
          startIcon={<CancelIcon />}
          disabled={loading || !extension || isExtensionClosed}
        >
          Void
        </Button>
        <Button
          variant="contained"
          color="secondary"
          testId="resend-extension-agreement-button"
          onClick={handleResend}
          startIcon={<Send />}
          disabled={loading || rows.length === 0 || isExtensionClosed}
        >
          Resend All Signing Links
        </Button>
      </Grid>

      {taskId && (
        <Grid item xs={12}>
          <Divider />
          <Box mt={2} mb={1}>
            <Typography variant="h6">Attachments</Typography>
          </Box>
          <ExtensionAgreementUpload document={document} onUploaded={refreshParentExtension} />
        </Grid>
      )}

      <ConfirmDialog
        open={showVoidConfirm}
        title="Void Extension Agreement"
        body="Are you sure you want to void this extension agreement request?"
        confirmButtonText="Yes"
        handleConfirm={handleVoid}
        handleCancel={() => setShowVoidConfirm(false)}
        handleClose={() => setShowVoidConfirm(false)}
      />
    </Grid>
  );

  return (
    <>
      {taskId ? (
        <CustomTaskDialog
          title={'Extension Agreement'}
          open={open}
          maxWidth={'md'}
          handleClose={handleDialogClose}
          dialogContent={dialogContent}
          taskId={taskId}
        />
      ) : (
        <StandardDialog
          title={'Extension Agreement'}
          open={open}
          maxWidth={'md'}
          handleClose={handleDialogClose}
          dialogContent={dialogContent}
        />
      )}

      <PDFPreview
        open={previewOpen}
        title={'Extension Agreement'}
        data={previewData}
        headers={{}}
        fileName={'Extension Agreement'}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewData(null);
        }}
      />
    </>
  );
});

export default ExtensionAgreementDialog;
